// frontend/admin-portal/src/components/procurement/ComparisonTable.jsx
import React from "react";
import { Badge } from "@fiberhome/shared-ui";

/**
 * comparison = {
 *   items: [{ rfqItemId, name, sku, quantity, unit }],
 *   suppliers: [{
 *     rfqId, supplier: { id, name }, quotation, total, currency,
 *     lines: [{ rfqItemId, status, unitPrice, lineTotal }],
 *     missingCount, missingRfqItemIds, unavailableRfqItemIds
 *   }],
 *   perItemCheapest: [{ rfqItemId, cheapest: { unitPrice, supplierId, supplierName } }],
 *   globallyMissingRfqItemIds: [ids]
 * }
 */
export default function ComparisonTable({ comparison }) {
  if (!comparison) return null;
  const { items, suppliers, perItemCheapest, globallyMissingRfqItemIds } =
    comparison;

  const cheapestMap = new Map(
    perItemCheapest.map((x) => [x.rfqItemId, x.cheapest]),
  );
  const globalMissing = new Set(globallyMissingRfqItemIds);

  return (
    <div className="fh-cmp-wrap">
      <table className="fh-cmp-table">
        <thead>
          <tr>
            <th className="fh-cmp-item-col">Item</th>
            <th className="fh-cmp-qty-col">Qty</th>
            {suppliers.map((s) => (
              <th key={s.rfqId} className="fh-cmp-supplier-col">
                <div className="fh-cmp-supplier-name">
                  {s.supplier?.name || `RFQ ${s.rfqCode}`}
                </div>
                {s.quotation ? (
                  <Badge variant={s.missingCount > 0 ? "warning" : "success"}>
                    {s.quotation.currency} {Number(s.total).toLocaleString()}
                  </Badge>
                ) : (
                  <Badge variant="default">no quote</Badge>
                )}
                {s.missingCount > 0 && (
                  <div className="fh-cmp-supplier-missing">
                    {s.missingCount} missing
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const cheapest = cheapestMap.get(it.rfqItemId);
            const isGloballyMissing = globalMissing.has(it.rfqItemId);
            return (
              <tr
                key={it.rfqItemId}
                className={isGloballyMissing ? "is-globally-missing" : ""}
              >
                <td className="fh-cmp-item-col">
                  <div style={{ fontWeight: 600 }}>{it.name}</div>
                  {it.sku && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {it.sku}
                    </div>
                  )}
                  {isGloballyMissing && (
                    <div className="fh-cmp-tag fh-cmp-tag--danger">
                      Not offered by any supplier
                    </div>
                  )}
                </td>
                <td className="fh-cmp-qty-col">
                  {it.quantity} {it.unit}
                </td>
                {suppliers.map((s) => {
                  const line = s.lines.find(
                    (l) => l.rfqItemId === it.rfqItemId,
                  );
                  const status = line?.status || "no_response";
                  const isCheapest =
                    cheapest &&
                    status === "available" &&
                    cheapest.supplierId === s.supplier.id &&
                    cheapest.unitPrice === line.unitPrice;

                  return (
                    <td
                      key={s.rfqId}
                      className={`fh-cmp-cell fh-cmp-cell--${status} ${isCheapest ? "is-cheapest" : ""}`}
                    >
                      {status === "available" && (
                        <>
                          <div className="fh-cmp-price">
                            {Number(line.unitPrice).toLocaleString()}
                          </div>
                          <div className="fh-cmp-line-total">
                            {Number(line.lineTotal).toLocaleString()}
                          </div>
                          {isCheapest && (
                            <span className="fh-cmp-cheapest">cheapest</span>
                          )}
                        </>
                      )}
                      {status === "unavailable" && (
                        <span className="fh-cmp-unavail">Not available</span>
                      )}
                      {status === "no_response" && (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="fh-cmp-totals-row">
            <td className="fh-cmp-item-col">
              <strong>Total</strong>
            </td>
            <td />
            {suppliers.map((s) => (
              <td key={s.rfqId} className="fh-cmp-cell">
                {s.quotation ? (
                  <strong>
                    {s.quotation.currency} {Number(s.total).toLocaleString()}
                  </strong>
                ) : (
                  <span className="text-muted">—</span>
                )}
                <div className="fh-cmp-covered">
                  {s.offeredCount} / {items.length} items
                </div>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
